import rawData from "../data/real-stars.json";
import type { PlanetData, StarData, StarSystem } from "../types/celestial";
import { celestialToSpatial } from "../types/celestial";

interface RawSystem {
	id: string;
	name: string;
	zone: number;
	systemIndex: number;
	ra: number;
	dec: number;
	star: {
		name: string;
		spectralClass: string;
		apparentMagnitude: number;
		absoluteMagnitude: number;
		distanceLightYears: number;
		stellarTemp?: number | null;
		stellarMass?: number | null;
		stellarRadius?: number | null;
	};
	planets: {
		id: string;
		name: string;
		orbitalRadius: number;
		orbitalPeriod: number;
		radius: number;
		mass?: number | null;
		discoveryMethod?: string;
		color: string;
		equilibriumTemp?: number | null;
		eccentricity?: number | null;
	}[];
}

const SOL_PLANETS: PlanetData[] = [
	{
		id: "sol-p1",
		name: "Mercury",
		orbitalRadius: 0.39,
		orbitalPeriod: 88,
		radius: 0.38,
		mass: 0.055,
		color: "#a0a0a0",
		equilibriumTemp: 440,
		eccentricity: 0.206,
	},
	{
		id: "sol-p2",
		name: "Venus",
		orbitalRadius: 0.72,
		orbitalPeriod: 225,
		radius: 0.95,
		mass: 0.815,
		color: "#e8c080",
		equilibriumTemp: 737,
		eccentricity: 0.007,
	},
	{
		id: "sol-p3",
		name: "Earth",
		orbitalRadius: 1.0,
		orbitalPeriod: 365,
		radius: 1.0,
		mass: 1.0,
		color: "#4a90d9",
		equilibriumTemp: 255,
		eccentricity: 0.017,
	},
	{
		id: "sol-p4",
		name: "Mars",
		orbitalRadius: 1.52,
		orbitalPeriod: 687,
		radius: 0.53,
		mass: 0.107,
		color: "#c46030",
		equilibriumTemp: 210,
		eccentricity: 0.093,
	},
	{
		id: "sol-p5",
		name: "Jupiter",
		orbitalRadius: 5.2,
		orbitalPeriod: 4333,
		radius: 11.21,
		mass: 317.8,
		color: "#c8a060",
		equilibriumTemp: 110,
		eccentricity: 0.049,
	},
	{
		id: "sol-p6",
		name: "Saturn",
		orbitalRadius: 9.54,
		orbitalPeriod: 10759,
		radius: 9.45,
		mass: 95.2,
		color: "#e0c890",
		equilibriumTemp: 81,
		eccentricity: 0.054,
	},
	{
		id: "sol-p7",
		name: "Uranus",
		orbitalRadius: 19.19,
		orbitalPeriod: 30687,
		radius: 4.01,
		mass: 14.5,
		color: "#80c8d8",
		equilibriumTemp: 58,
		eccentricity: 0.047,
	},
	{
		id: "sol-p8",
		name: "Neptune",
		orbitalRadius: 30.07,
		orbitalPeriod: 60190,
		radius: 3.88,
		mass: 17.1,
		color: "#4060c0",
		equilibriumTemp: 46,
		eccentricity: 0.009,
	},
];

const SOL_STAR: StarData = {
	name: "Sol",
	spectralClass: "G2V",
	apparentMagnitude: -26.74,
	absoluteMagnitude: 4.83,
	distanceLightYears: 0,
	stellarTemp: 5778,
	stellarMass: 1,
	stellarRadius: 1,
};

function parseSystem(raw: RawSystem): StarSystem {
	const { x, y } = celestialToSpatial(raw.ra, raw.dec, raw.star.distanceLightYears);
	return {
		id: raw.id,
		name: raw.name,
		star: raw.star,
		planets: raw.planets,
		x,
		y,
		zone: raw.zone,
		systemIndex: raw.systemIndex,
	};
}

/** Load all star systems including Sol */
export function loadStarSystems(): StarSystem[] {
	const sol: StarSystem = {
		id: "sol",
		name: "Solar System",
		star: SOL_STAR,
		planets: SOL_PLANETS,
		x: 0,
		y: 0,
		zone: 0,
		systemIndex: 0,
	};

	const systems = [sol, ...(rawData as { systems: RawSystem[] }).systems.map(parseSystem)];
	return systems;
}
